import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { useApi, useAuth } from '../../hooks';
import { User } from '../../models/user';
import {
  EnrollmentStatus,
  OnboardingStatus,
  InitialVisitStatus,
  SortBy,
  SortOrder,
} from '../../components/HealthCoachDashboard/constants';

type Props = {
  care_team_member_id?: string;
  coach_id?: string;
  care_model?: string;
  user_type?: string;
  search?: string;
  meetings?: string;
  meeting_num_days?: number;
  meeting_confirmed?: boolean;
  offset?: number;
  limit?: number;
  enrollment_status?: EnrollmentStatus;
  app_onboarding_status?: OnboardingStatus;
  initial_visit?: InitialVisitStatus;
  risk_stratification?: string;
  sort_by?: SortBy;
  sort_order?: SortOrder;
};

export const useUsersList = ({
  care_team_member_id,
  coach_id,
  care_model,
  user_type,
  search,
  meetings,
  meeting_num_days,
  meeting_confirmed,
  offset,
  limit,
  enrollment_status,
  app_onboarding_status,
  initial_visit,
  risk_stratification,
  sort_by,
  sort_order,
}: Props) => {
  const { api } = useApi();
  const { isAuthenticated, loading, userToken } = useAuth();

  const queryStr = qs.stringify(
    {
      care_team_member_id,
      coach_id,
      care_model,
      user_type,
      search,
      meetings,
      meeting_num_days,
      meeting_confirmed,
      offset,
      limit,
      enrollment_status,
      app_onboarding_status,
      initial_visit,
      risk_stratification,
      sort_by,
      sort_order,
    },
    { arrayFormat: 'repeat' }
  );

  const fetchUsersList = async () => {
    const requestObj = {
      route: `/private/coach/users${queryStr ? `?${queryStr}` : ''}`,
      token: userToken,
    };

    return await api(requestObj);
  };

  const { data, isLoading, isFetching, isError } = useQuery<
    { users: User[]; total_results: number },
    Error
  >(['hc-users-list', userToken, queryStr], () => fetchUsersList(), {
    enabled: isAuthenticated && !loading && !!userToken,
    refetchOnWindowFocus: false,
    // Keep showing the current page's data while the next page/filter
    // fetches, instead of dropping back to isLoading:true and tearing
    // down the toolbar/list on every pagination or filter change.
    keepPreviousData: true,
  });

  const pageLength = data?.users?.length;

  return {
    usersList: data?.users,
    isLoading,
    isFetching,
    isError,
    pageLength,
    totalResults: data?.total_results,
  };
};
